import React from 'react';

interface CampaignProgressBarProps {
  current: number;
  total: number;
  showPercentage?: boolean;
  /** Linha de labels acima da barra (X/Y à esquerda + % à direita). O card de
      campanha desliga — o mockup usa UMA linha "X / Y · Z%" abaixo da barra. */
  showLabels?: boolean;
  animated?: boolean;
}

/**
 * Campaign Progress Bar Component
 * Displays progress bar with animated shimmer effect
 *
 * @example
 * <CampaignProgressBar current={750} total={1000} animated />
 */
export const CampaignProgressBar: React.FC<CampaignProgressBarProps> = ({
  current = 0,
  total = 0,
  showPercentage = true,
  showLabels = true,
  animated = true,
}) => {
  const safeTotal = total ?? 0;
  const safeCurrent = current ?? 0;
  const percentage = safeTotal > 0 ? Math.min((safeCurrent / safeTotal) * 100, 100) : 0;

  return (
    <div className="space-y-1.5">
      {/* Labels */}
      {showLabels && (
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium" style={{ color: 'var(--theme-card-text)' }}>
            {safeCurrent.toLocaleString()} / {safeTotal.toLocaleString()}
          </span>
          {showPercentage && (
            <span className="text-xs font-semibold" style={{ color: 'var(--theme-primary)' }}>
              {percentage.toFixed(1)}%
            </span>
          )}
        </div>
      )}

      {/* Progress Bar */}
      <div
        className="h-2 rounded-full overflow-hidden"
        style={{ backgroundColor: 'var(--theme-button-ghost-hover)' }}
      >
        <div
          className={`h-full transition-all duration-500 ease-out rounded-full ${
            animated ? 'animate-shimmer' : ''
          }`}
          style={{
            width: `${percentage}%`,
            backgroundColor: 'var(--theme-primary)',
            backgroundImage: animated
              ? 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)'
              : undefined,
            backgroundSize: '200% 100%',
          }}
        />
      </div>

      {/* CSS Animation */}
      <style>{`
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        .animate-shimmer {
          animation: shimmer 2s infinite;
        }
      `}</style>
    </div>
  );
};
